/* eslint-disable react/function-component-definition */
import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faEdit } from "@fortawesome/free-solid-svg-icons"; // Added faEdit for edit button
import { Entry } from "../../redux/entrySlice";
import SelectBox from "../../components/SelectBox/SelectBox";
import PhoneNumberInput from "../../components/PhoneNumberBox/PhoneNumberBox";
import styles from "./editUserPopUp.module.scss";
import { TableContext } from "../../contexts/tableContext";
import MultipleFileUpload from "../../components/MultipleFileUpload/MultipleFileUpload";
import "react-toastify/dist/ReactToastify.css";
import TextInput from "../../components/TextField/TextField";
import EmailInput from "../../components/EmailBox/EmailBox";
import { REACT_APP_API_ROLES_DROPDOWN } from "../../redux/apiConstants";
import { Image } from "react-bootstrap";
import Loader from '../../components/Loader/Loader';
import SuspendUserModal from "../../components/SuspendUserModal/SuspendUserModal";
import { confirmAlert } from "react-confirm-alert";

const EditUserPopup = React.memo(
  ({ userId, handleClose, setIsUpdated, edit = true }) => {
    const [submitting, setSubmitting] = useState(false);
    const { formthemeStyle, bodyheader, viewformStyle } =
      useContext(TableContext);
    const [user, setUser] = useState([]);
    const [error, setError] = useState(null);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const dispatch = useDispatch();
    const { files } = useContext(TableContext);
    const {
      control,
      register,
      handleSubmit,
      formState: { errors },
      reset,
    } = useForm();

    useEffect(() => {
      setLoading(true);
      const url2 = REACT_APP_API_ROLES_DROPDOWN; // api url
      fetch(url2)
        .then((resp) => resp.json())
        .then((resp) => {
          setRoles(resp.data.role);
        });

      const actionData = { actionUrl: `user/${userId}`, actionMethod: "get" };
      dispatch(Entry(actionData)).then((resp) => {
        if (resp.payload && resp.payload.data && resp.payload.data.user) {
          const userData = resp.payload.data.user;
          setUser(userData); // Set user data including userType
          reset(userData); // Reset the form with user data
          setLoading(false);
        } else {
          console.error("User data not found in response:", resp);
          setUser(null);
          setLoading(false);
        }
      });
    }, [userId]);

    const onSubmitdata = (data) => {
      if (data.name.trim().length === 0) {
        document.getElementById("name").focus();
      } else {
        setSubmitting(true);
        setError(null);
        const apiData = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          File: files,
        };
        data.actionUrl = `user/${userId}`;
        data.actionMethod = "patch";
        data.apiData = apiData;
        dispatch(Entry(data)).then((resp) => {
          setSubmitting(false);
          if (resp.payload.code === 200) {
            toast.success("User details updated successfully");
            setIsEditMode(false);
            handleClose();
            setIsUpdated(true);
          } else {
            setError(resp.payload.message);
            toast.error("Error updating user details");
          }
        });
      }
    };

    const handleSuspendUser = () => {
      if (!user?._id || user?.isSuspend) {
        return;
      }
      setShowSuspendModal(true);
    };

    const handleActivateUser = () => {
      if (!user?._id || !user?.isSuspend) {
        return;
      }

      confirmAlert({
        title: "Activate Account",
        message: "Are you sure you want to activate this user account?",
        buttons: [
          {
            label: "Yes",
            onClick: () => {
              setSubmitting(true);
              const actionData = {
                actionUrl: `user/${user._id}/activate`,
                actionMethod: "patch",
                apiData: {},
              };

              dispatch(Entry(actionData)).then((resp) => {
                setSubmitting(false);
                if (resp?.payload?.code === 200) {
                  setUser((prevUser) => ({
                    ...prevUser,
                    isSuspend: false,
                    suspendReason: "",
                  }));
                  toast.success("User account activated successfully");
                  setIsUpdated(true);
                } else {
                  toast.error(
                    resp?.payload?.message || "Failed to activate user"
                  );
                }
              });
            },
          },
          {
            label: "No",
          },
        ],
      });
    };

    const handleSuspendSuccess = () => {
      setUser((prevUser) => ({
        ...prevUser,
        isSuspend: true,
      }));
      setIsUpdated(true);
    };

    return (
      <div className={styles.popupbox}>
        <div className={styles.box} id={styles.box}>
          {/* Flex container for close button, heading, and edit button */}
          <div className="d-flex align-items-center gap-3">
            <button
              className={styles.closeicon}
              aria-label="Close"
              onClick={handleClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            {/* Conditionally render the heading based on isEditMode */}
            {user.image && !isEditMode && (
              <Image
                src={user.image}
                alt="logo"
                className={styles.profilepic}
              />
            )}
            <h2 className={styles.heading}>
              {isEditMode ? "Edit User Details" : "User Details"}
            </h2>

            {/* Show edit button only when not in edit mode */}
            {!isEditMode && edit && (
              <button
                className={`${styles.formbtnedit}`} // Add your custom class for styling
                onClick={() => setIsEditMode(true)}
                aria-label="Edit"
              >
                <FontAwesomeIcon icon={faEdit} /> {/* Edit icon */}
                Edit
              </button>
            )}
          </div>

          <div className="container-fluid mt-5 edituserform">
            {loading ? (
              <div className="text-center mt-5">
                <Loader />
                <p>Loading user data...</p>
              </div>
            ) : user ? (
              <>
              <form onSubmit={handleSubmit(onSubmitdata)}>
                <div className={`row ${styles.profilepicdiv}`}></div>
                <div className="row">
                  <div className="col-md-6 col-sm-6">
                    <TextInput
                      className={
                        isEditMode
                          ? styles.inputboxedit
                          : styles.inputboxnotedit
                      }
                      label="Name"
                      fieldName="name"
                      placeHolder="Enter Name"
                      register={register}
                      errors={errors}
                      isRequired
                      mandatory
                      maximLength="50"
                      readOnly={!isEditMode} // Disable input when not in edit mode
                    />
                  </div>
                  <div className="col-md-6 col-sm-6">
                    <EmailInput
                      className={
                        isEditMode
                          ? styles.inputboxedit
                          : styles.inputboxnotedit
                      }
                      label="Email"
                      fieldName="email"
                      register={register}
                      errors={errors}
                      isRequired
                      mandatory
                      placeHolder="Email"
                      readOnly={!isEditMode} // Disable input when not in edit mode
                    />
                  </div>
                </div>
                <div className="row">
                  {/* Phone Number Input */}
                  <div className="col-md-6 col-sm-6">
                    <PhoneNumberInput
                      label="Phone Number"
                      placeHolder="Phone Number"
                      className={
                        isEditMode
                          ? styles.inputboxedit
                          : styles.inputboxnotedit
                      }
                      fieldName="phone"
                      register={register}
                      errors={errors}
                      isRequired
                      mandatory
                      readOnly={!isEditMode} // Disable input when not in edit mode
                    />
                  </div>

                  {/* User Type */}
                  {/* <div className="col-md-6 col-sm-6">
                  <label>User Type</label>
                  <div className={`${styles.inputbox} ${isEditMode ? `${styles.boxDisabled}` : ''}`}>
                    <div className={`${styles.userTypeValue}`}>
                      <span>{user.userType || "N/A"}</span>
                    </div>
                  </div>
                </div> */}
                </div>
                {isEditMode && (
                  <div className={`row ${styles.fileContainer}`}>
                    <div className="col-md-6 col-sm-6">
                      <MultipleFileUpload
                        editFiles={user.image}
                        section="user"
                        disabled={!isEditMode}
                        register={register}
                        errors={errors}
                      />
                    </div>
                  </div>
                )}
                {/* Submit button visible only in edit mode */}
                {isEditMode && (
                  <div className={styles.btnContainer}>
                    <input
                      className={styles.formbtn}
                      type="submit"
                      disabled={submitting}
                      value={submitting ? "Please wait.." : "Update"}
                    />
                  </div>
                )}
                {!isEditMode && (
                  <div className={styles.btnContainer}>
                    {user?.isSuspend ? (
                      <button
                        type="button"
                        className={styles.activateBtn}
                        onClick={handleActivateUser}
                        disabled={submitting}
                      >
                        Activate Account
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={styles.suspendBtn}
                        onClick={handleSuspendUser}
                        disabled={submitting}
                      >
                        Suspend User
                      </button>
                    )}
                  </div>
                )}

                {error && <h6 className={styles.errormsg}>{error}</h6>}
              </form>

              <SuspendUserModal
                show={showSuspendModal}
                handleClose={() => setShowSuspendModal(false)}
                userId={user?._id}
                onSuccess={handleSuspendSuccess}
              />
              </>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  marginTop: "130px",
                  color: "#39979d",
                }}
              >
                <Loader />
                <p>No user data found</p>
              </div>
            )}

            {/* <ToastContainer autoClose={2000} /> */}
          </div>
        </div>
      </div>
    );
  }
);

export default EditUserPopup;
